import {
	SovosDivider,
	SovosMenuItem,
	SovosRadioGroup,
	SovosSelect,
	SovosTypography,
} from 'mosaic-react';
import { useEffect, useState } from 'react';

import type { RateFileOutput } from 'src/interfaces/Configuration';
import type { NewRateFileFormStep } from 'src/pages/newRateFile/NewRateFileFormTypes';
import type { NewRateFileFormState } from 'src/pages/newRateFile/NewRateFileFormTypes';

import FormStepLayout from './FormStepLayout';

const StepFileFormat: NewRateFileFormStep<{
	selectedFormat: NewRateFileFormState['outputFormat'];
	setSelectedFormat: (selectedFormat: { name: string; id: number }) => void;
	selectedSettings: NewRateFileFormState['settings'];
	setSelectedSettings: (settings: NewRateFileFormState['settings']) => void;
	outputFormats: RateFileOutput[];
	outputFormatsIsLoading: boolean;
}> = ({
	onContinue,
	selectedFormat,
	setSelectedFormat,
	selectedSettings,
	setSelectedSettings,
	outputFormats,
	outputFormatsIsLoading,
}) => {
	const [showErrors, setShowErrors] = useState(false);

	const validations = [
		{
			isValid: Boolean(selectedFormat),
			message: 'Please select a format',
		},
	];

	const errors = validations
		.filter(({ isValid }) => !isValid)
		.map(({ message }) => message);

	const handleContinue = () => {
		if (errors.length === 0) {
			onContinue();
		} else if (!showErrors) {
			setShowErrors(true);
		}
	};

	useEffect(() => {
		if (!selectedFormat?.name.includes('xStore')) {
			setSelectedSettings({ ...selectedSettings, xstoreMultiFile: false });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedFormat]);

	useEffect(() => {
		setSelectedSettings({
			...selectedSettings,
			xstoreMultiFile: selectedSettings?.xstoreMultiFile ?? false,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<FormStepLayout
			title='Rate file format'
			subTitle='Select the format you would like the rate file to export as'
			onContinue={handleContinue}
			errors={errors}
			showErrors={showErrors}
		>
			<SovosSelect
				data-testid='rateFileOutputFormatsSelect'
				label={
					<>
						Format<span aria-hidden='true'> *</span>
					</>
				}
				sx={{
					maxWidth: '400px',
					'&&& label': {
						color:
							showErrors && !validations[0].isValid ? 'error.main' : undefined,
					},
				}}
				value={selectedFormat?.id}
				onChange={(event) => {
					const format = outputFormats.find(
						(format) => Number(event.target.value) === format.id
					);

					if (format) {
						setSelectedFormat({
							name: format.name,
							id: format.id,
						});
					}
				}}
				error={showErrors && !validations[0].isValid}
			>
				{outputFormatsIsLoading ? (
					<SovosMenuItem disabled value='loading'>
						Loading...
					</SovosMenuItem>
				) : (
					outputFormats.map(({ id, name }) => (
						<SovosMenuItem key={id} value={id}>
							{name}
						</SovosMenuItem>
					))
				)}
			</SovosSelect>

			{selectedFormat?.id && selectedFormat?.name.includes('xStore') && (
				<>
					<SovosDivider sx={{ my: 3, alignSelf: 'stretch' }} />
					<SovosTypography variant='h5' sx={{ mb: 3 }}>
						How would you like to download your file?
					</SovosTypography>
					<SovosRadioGroup
						options={[
							{
								label: 'As a single file',
								value: 'false',
							},
							{
								label: 'As multiple files, split by type',
								value: 'true',
							},
						]}
						value={selectedSettings?.xstoreMultiFile}
						onChange={(_: any, value: string) => {
							setSelectedSettings({
								...selectedSettings,
								xstoreMultiFile: !!(value && value.toLowerCase() === 'true'),
							});
							setShowErrors(false);
						}}
					/>
				</>
			)}
		</FormStepLayout>
	);
};

export default StepFileFormat;
